import React, { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HelpOutline as HelpIcon,
  AccessTime as AccessTimeIcon,
  Search as SearchIcon,
  InfoOutlined as InfoOutlinedIcon,
  ArrowUpward as ArrowUpwardIcon
} from "@mui/icons-material";
import {
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Card,
  CardContent,
  IconButton,
  LinearProgress
} from "@mui/material";
import axios from "axios";
import { decryptData } from "../../crypto";
import { fetchExamResultApi } from "../../services/service";
import { toast } from "react-toastify";
import ROUTES_NAME from "../../constants/RoutesName";

const styles = {
  container: {
    padding: "24px",
    background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
    minHeight: "100vh",
    fontFamily: "'Outfit', 'Roboto', sans-serif",
  },
  header: {
    marginBottom: "24px",
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  backButton: {
    backgroundColor: "white",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    "&:hover": {
      backgroundColor: "#f1f3f5",
    },
  },
  pageTitle: {
    fontWeight: 700,
    background: "linear-gradient(45deg, #1e3c72 0%, #2a5298 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  card: {
    padding: "20px",
    borderRadius: "16px",
    boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.08)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    background: "rgba(255, 255, 255, 0.9)",
    backdropFilter: "blur(4px)",
    height: "100%",
  },
  statLabel: {
    color: "#6c757d",
    fontSize: "0.85rem",
    textTransform: "uppercase",
    letterSpacing: "1px",
    fontWeight: 600,
  },
  statValue: {
    fontWeight: 700,
    fontSize: "1.8rem",
    marginTop: "8px",
    color: "#1e3c72",
  },
  statusBadge: {
    padding: "6px 16px",
    borderRadius: "20px",
    fontSize: "0.85rem",
    fontWeight: 700,
    textTransform: "uppercase",
    display: "inline-block",
  },
  passedBadge: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
  },
  failedBadge: {
    backgroundColor: "#ffebee",
    color: "#c62828",
  },
  chartContainer: {
    minHeight: "300px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  questionCard: {
    marginBottom: "16px",
    borderRadius: "12px",
    border: "1px solid #e9ecef",
    transition: "box-shadow 0.2s ease",
    "&:hover": {
      boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
    },
  },
  questionHeader: {
    padding: "16px",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#f8f9fa",
    borderRadius: "12px 12px 0 0",
  },
  questionBody: {
    padding: "20px",
    backgroundColor: "#ffffff",
    borderRadius: "0 0 12px 12px",
    borderTop: "1px solid #f1f3f5",
  },
  optionItem: {
    padding: "12px 16px",
    borderRadius: "8px",
    marginBottom: "8px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    border: "1px solid #dee2e6",
  },
  correctOption: {
    backgroundColor: "#e8f5e9",
    borderColor: "#a5d6a7",
  },
  incorrectOption: {
    backgroundColor: "#ffebee",
    borderColor: "#ef9a9a",
  },
  selectedOption: {
    borderWidth: "2px",
    borderColor: "#2196f3",
  },
  optionLabel: {
    fontWeight: 600,
    width: "30px",
    height: "30px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.9rem",
  },
  badgeCorrect: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    padding: "4px 10px",
    borderRadius: "6px",
    fontWeight: 600,
  },
  badgeIncorrect: {
    backgroundColor: "#ffebee",
    color: "#c62828",
    padding: "4px 10px",
    borderRadius: "6px",
    fontWeight: 600,
  },
  badgeUnattempted: {
    backgroundColor: "#f1f3f5",
    color: "#6c757d",
    padding: "4px 10px",
    borderRadius: "6px",
    fontWeight: 600,
  },
  barHover: {
    opacity: 0.85,
    cursor: "pointer",
  }
};

export default function ExamResult() {
  const classes = styles;
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const examId = params.examId || location.state?.examId;
  const teacherId = params.teacherId || sessionStorage.getItem("userId");

  const [searchQuery, setSearchQuery] = useState("");
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [comparisonData, setComparisonData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");
  const [hoveredBar, setHoveredBar] = useState(null);

  useEffect(() => {
    const fetchAnalysisData = async () => {
      if (!examId) {
        return;
      }
      setLoading(true);
      try {
        const res = await fetchExamResultApi(examId);
        if (res && res.success && res.result && res.result.length > 0) {
          setResultData(res);
        }
      } catch (error) {
        console.error("Error fetching exam result analysis:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchComparisonData = async () => {
      if (!teacherId) return;
      try {
        const decryptedToken = decryptData(sessionStorage.getItem("examToken"));
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/compare/user/with/topper/${teacherId}`,
          {
            headers: {
              Authorization: `Bearer ${decryptedToken}`,
            },
          }
        );
        if (response.data && response.data.success) {
          // Flatten dictionary response, excluding metadata fields like 'success'
          const dataList = Object.keys(response.data)
            .filter((key) => key !== "success")
            .map((key) => response.data[key]);
          setComparisonData(dataList);
        }
      } catch (error) {
        console.error("Error fetching competitive comparison data:", error);
      }
    };

    fetchAnalysisData();
    fetchComparisonData();
  }, [teacherId, examId]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const summary = useMemo(() => resultData?.summary || {}, [resultData]);
  const sectionAnalysis = useMemo(() => resultData?.section_analysis || {}, [resultData]);
  const topperAnalysis = useMemo(() => resultData?.topper_analysis || {}, [resultData]);
  const questions = useMemo(() => resultData?.result || [], [resultData]);

  // Find the candidate name from metadata
  const candidateName = useMemo(() => {
    return sectionAnalysis.full_name || "Teacher";
  }, [sectionAnalysis]);

  // Process the questions in resultData.result
  const processedQuestions = useMemo(() => {
    if (!questions) return [];

    return questions.map((q, idx) => {
      const sel = q.selected_option_ids || [];

      // Get all correct option IDs from both correct_option_ids and the options list (where correct_option === true)
      const corrFromOptions = (q.options || [])
        .filter(o => o.correct_option === true || o.correct_option === "true" || o.correct_option === 1)
        .map(o => o.id);

      const normalizedCorr = Array.from(new Set([
        ...(q.correct_option_ids || []).map(String),
        ...corrFromOptions.map(String)
      ]));

      const normalizedSel = sel.map(String);

      let status = "Unattempted";
      
      // Determine if it is a Fill In The Blanks / Numerical text-based response
      const isFillInTheBlanks = q.question_type === "Fill In The Blanks" || (normalizedSel.length === 0 && q.answer !== null && q.answer !== undefined && String(q.answer).trim() !== "");
      
      if (isFillInTheBlanks) {
        const studentAns = String(q.answer || "").trim().toLowerCase();
        if (studentAns !== "") {
          const correctOptions = (q.options || []).filter(
            o => o.correct_option === true || o.correct_option === "true" || o.correct_option === 1
          );
          const isCorrect = correctOptions.some(o => 
            studentAns === String(o.solution_en || "").trim().toLowerCase() ||
            studentAns === String(o.solution_hi || "").trim().toLowerCase() ||
            studentAns === String(o.option_en || "").trim().toLowerCase() ||
            studentAns === String(o.option_hi || "").trim().toLowerCase()
          );
          status = isCorrect ? "Correct" : "Incorrect";
        }
      } else if (normalizedSel.length > 0) {
        const isCorrect =
          normalizedSel.length === normalizedCorr.length &&
          normalizedSel.every((val) => normalizedCorr.includes(val));
        status = isCorrect ? "Correct" : "Incorrect";
      }

      return {
        ...q,
        index: idx + 1,
        status,
        selectedOptionIds: sel,
        correctOptionIds: normalizedCorr.map(val => isNaN(val) ? val : Number(val)),
      };
    });
  }, [questions]);

  // Filtered questions list
  const filteredQuestions = useMemo(() => {
    return processedQuestions.filter((q) => {
      const matchesStatus = statusFilter === "All" || q.status === statusFilter;
      const questionText = q.question_en || "";
      const matchesSearch = questionText
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      return matchesStatus && matchesSearch;
    });
  }, [processedQuestions, searchQuery, statusFilter]);

  // Topic Breakdown calculations
  const topicBreakdown = useMemo(() => {
    const topicsMap = {};
    processedQuestions.forEach((q) => {
      const topicName = q.examTopic?.topic_name || "Unknown Topic";
      if (!topicsMap[topicName]) {
        topicsMap[topicName] = {
          topicName,
          count: 0,
          attempted: 0,
          notAttempted: 0,
          correctCount: 0,
          incorrectCount: 0
        };
      }
      topicsMap[topicName].count += 1;
      if (q.status === "Unattempted") {
        topicsMap[topicName].notAttempted += 1;
      } else {
        topicsMap[topicName].attempted += 1;
        if (q.status === "Correct") {
          topicsMap[topicName].correctCount += 1;
        } else if (q.status === "Incorrect") {
          topicsMap[topicName].incorrectCount += 1;
        }
      }
    });

    return Object.values(topicsMap).map((row) => {
      const pct = (row.correctCount / row.count) * 100;
      let statusCategory = "Weak";
      if (pct >= 70) {
        statusCategory = "Strong";
      } else if (pct >= 50) {
        statusCategory = "Moderate";
      }
      return {
        ...row,
        statusCategory
      };
    });
  }, [processedQuestions]);

  // Aggregate dynamically calculated stats
  const dynamicStats = useMemo(() => {
    const total = processedQuestions.length;
    const correct = processedQuestions.filter((q) => q.status === "Correct").length;
    const incorrect = processedQuestions.filter((q) => q.status === "Incorrect").length;
    const unattempted = processedQuestions.filter((q) => q.status === "Unattempted").length;

    // Use dynamic values from section analysis for totalMarks & percentage
    const sections = sectionAnalysis.section_analysis || [];
    const calculatedTotalMarks = sections.reduce((sum, sec) => sum + (sec.total_marks || 0), 0);
    const totalMarks = summary?.total_marks;

    const receivedMarks = summary.received_marks ?? 0;
    const percentage = summary?.percentage;
    const resultStatus = summary.result_status ?? "fail";
    const timeTaken = summary.time_taken ?? "0:14";

    return {
      total,
      correct,
      incorrect,
      unattempted,
      receivedMarks,
      totalMarks,
      percentage,
      resultStatus,
      timeTaken,
    };
  }, [processedQuestions, summary, sectionAnalysis]);



  // Competitive Analysis data mapping teacher vs topper across multiple exams
  const sectionChartData = useMemo(() => {
    if (!comparisonData || comparisonData.length === 0) {
      return [];
    }

    return comparisonData.map((item) => {
      // Clean exam name for shorter display if needed, or use directly
      const cleanName = item.exam_name
        ? (item.exam_name.length > 25 ? item.exam_name.substring(0, 22) + "..." : item.exam_name)
        : `Exam ${item.exam_id}`;
      return {
        name: cleanName,
        teacherMarks: item.user_marks || 0,
        topperMarks: item.topper_marks || 0,
        totalMarks: item.total_score || 100
      };
    });
  }, [comparisonData]);

  // Custom SVG donut chart calculations for Question Attempt Breakdown
  const donutChartData = useMemo(() => {
    const total = dynamicStats.total || 1;
    const cVal = dynamicStats.correct;
    const iVal = dynamicStats.incorrect;
    const uVal = dynamicStats.unattempted;

    const r = 50;
    const circ = 2 * Math.PI * r;

    const cShare = (cVal / total) * circ;
    const iShare = (iVal / total) * circ;
    const uShare = (uVal / total) * circ;

    return {
      circ,
      cShare,
      iShare,
      uShare,
      cOffset: 0,
      iOffset: -cShare,
      uOffset: -(cShare + iShare),
    };
  }, [dynamicStats]);

  if (loading || !resultData) {
    return (
      <div className={classes.container}>
        <LinearProgress style={{ height: 4 }} />
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
          <Typography variant="h6" style={{ color: "#1e3c72" }}>
            Loading Detailed Performance Analysis...
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div style={classes.container}>
      {/* Header */}
      <div style={classes.header}>
        <IconButton
          sx={classes.backButton}
          onClick={() => navigate(-1)}
        >
          <ArrowBackIcon style={{ color: "#1e3c72" }} />
        </IconButton>
        <div>
          <Typography variant="h4" sx={classes.pageTitle}>
            Detailed Candidate Response Sheet
          </Typography>
          <Typography variant="subtitle2" style={{ color: "#6c757d", marginTop: 4 }}>
            Detailed breakdown of answers, correct options, and section benchmarking
          </Typography>
        </div>
      </div>

      <Grid container spacing={3} style={{ marginBottom: "24px" }}>
        <Grid item xs={12} sm={4} md={4} style={{ width: "35%" }}>
          <Paper sx={classes.card}>
            <Typography sx={classes.statLabel}>Time Spent</Typography>
            <Typography sx={classes.statValue}>
              {dynamicStats.timeTaken}
            </Typography>
            <div style={{ marginTop: "12px", display: "flex", alignItems: "center", gap: 4 }}>
              <AccessTimeIcon style={{ fontSize: 16, color: "#6c757d" }} />
              <Typography variant="caption" style={{ color: "#6c757d" }}>
                Total duration limit: 120 mins
              </Typography>
            </div>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4} md={4} style={{ width: "35%" }}>
          <Paper sx={classes.card}>
            <Typography sx={classes.statLabel}>Score</Typography>
            <Typography sx={classes.statValue}>
              {dynamicStats.receivedMarks} / {dynamicStats.totalMarks}
            </Typography>
            <div style={{ marginTop: "12px" }}>
              <Typography variant="caption" style={{ color: "#2e7d32", fontWeight: 600 }}>
                {/* {Math.round((dynamicStats.correct / (dynamicStats.correct + dynamicStats.incorrect || 1)) * 100)}% Accuracy rate */}
              </Typography>
            </div>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4} md={4} style={{ width: "25%" }}>
          <Paper sx={classes.card}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>
                <Typography sx={classes.statLabel}>Percentage</Typography>
                <Typography sx={classes.statValue}>
                  {dynamicStats.percentage}%
                </Typography>
              </div>
              {/* <div
                style={{
                  ...classes.statusBadge,
                  ...(dynamicStats.resultStatus?.toLowerCase() === "pass"
                    ? classes.passedBadge
                    : classes.failedBadge),
                  alignSelf: "flex-start"
                }}
              >
                {dynamicStats.resultStatus}
              </div> */}
            </div>
            <div style={{ marginTop: "12px" }}>
              <Typography variant="caption" style={{ color: "#6c757d" }}>
                {/* Obtained {dynamicStats.receivedMarks} of {dynamicStats.totalMarks} marks */}
              </Typography>
            </div>
          </Paper>
        </Grid>
      </Grid>

      {/* Visual Graphs Section */}
      <Grid container spacing={3} style={{ marginBottom: "24px" }}>
        {/* Donut Chart (Breakdown) */}
        <Grid item xs={12} md={4}>
          <Paper sx={classes.card}>
            <Typography variant="h6" style={{ fontWeight: 700, marginBottom: "16px", color: "#1e3c72" }}>
              Question Breakdown
            </Typography>
            <div style={classes.chartContainer}>
              <svg width="200" height="200" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="50" fill="none" stroke="#f1f3f5" strokeWidth="12" />
                {/* Correct Answers */}
                <circle
                  cx="60"
                  cy="60"
                  r="50"
                  fill="none"
                  stroke="#2e7d32"
                  strokeWidth="12"
                  strokeDasharray={`${donutChartData.cShare} ${donutChartData.circ}`}
                  strokeDashoffset={donutChartData.cOffset}
                  transform="rotate(-90 60 60)"
                />
                {/* Incorrect Answers */}
                <circle
                  cx="60"
                  cy="60"
                  r="50"
                  fill="none"
                  stroke="#c62828"
                  strokeWidth="12"
                  strokeDasharray={`${donutChartData.iShare} ${donutChartData.circ}`}
                  strokeDashoffset={donutChartData.iOffset}
                  transform="rotate(-90 60 60)"
                />
                {/* Unattempted Answers */}
                <circle
                  cx="60"
                  cy="60"
                  r="50"
                  fill="none"
                  stroke="#bdbdbd"
                  strokeWidth="12"
                  strokeDasharray={`${donutChartData.uShare} ${donutChartData.circ}`}
                  strokeDashoffset={donutChartData.uOffset}
                  transform="rotate(-90 60 60)"
                />
                {/* Center text */}
                <text x="60" y="58" textAnchor="middle" fontSize="11" fontWeight="700" fill="#1e3c72">
                  {dynamicStats.correct}/{dynamicStats.total}
                </text>
                <text x="60" y="72" textAnchor="middle" fontSize="7" fill="#6c757d" fontWeight="600">
                  CORRECT
                </text>
              </svg>

              <div style={{ display: "flex", gap: "16px", marginTop: "24px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <div style={{ width: "12px", height: "12px", borderRadius: "3px", backgroundColor: "#2e7d32" }} />
                  <Typography variant="body2" style={{ fontWeight: 600, color: "#495057" }}>
                    Correct ({dynamicStats.correct})
                  </Typography>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <div style={{ width: "12px", height: "12px", borderRadius: "3px", backgroundColor: "#c62828" }} />
                  <Typography variant="body2" style={{ fontWeight: 600, color: "#495057" }}>
                    Incorrect ({dynamicStats.incorrect})
                  </Typography>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <div style={{ width: "12px", height: "12px", borderRadius: "3px", backgroundColor: "#bdbdbd" }} />
                  <Typography variant="body2" style={{ fontWeight: 600, color: "#495057" }}>
                    Unattempted ({dynamicStats.unattempted})
                  </Typography>
                </div>
              </div>
            </div>
          </Paper>
        </Grid>

        {/* Section Comparison Bar Chart */}
        <Grid item xs={12} md={8} style={{ width: "65%" }}>
          <Paper sx={classes.card}>
            <Typography variant="h6" style={{ fontWeight: 700, marginBottom: "16px", color: "#1e3c72" }}>
              Comparative Analysis (Your Marks vs Topper Marks)
            </Typography>

            <div style={{ position: "relative", height: "300px", padding: "16px 0", boxSizing: "border-box" }}>
              {sectionChartData.length > 0 ? (
                <>
                  <svg width="100%" height="100%" viewBox="0 0 600 250" preserveAspectRatio="none">
                    <style>{`
                      .bar-hover {
                        opacity: 0.85;
                        cursor: pointer;
                        transition: opacity 0.2s ease;
                      }
                      .bar-hover:hover {
                        opacity: 1;
                      }
                    `}</style>
                    {/* Grid Lines */}
                    {[0, 1, 2, 3, 4].map((g) => (
                      <line
                        key={g}
                        x1="50"
                        y1={30 + g * 40}
                        x2="580"
                        y2={30 + g * 40}
                        stroke="#e9ecef"
                        strokeWidth="1"
                      />
                    ))}

                    {/* Render Columns */}
                    {(() => {
                      const maxPossible = Math.max(...sectionChartData.map(d => Math.max(d.teacherMarks, d.topperMarks)), 10);

                      return sectionChartData.map((item, idx) => {
                        const colWidth = 24;
                        // Keep columns close together (e.g. 80px spacing) instead of stretching across the entire width
                        const spacing = 80;
                        const xCoord = 70 + idx * spacing;

                        const teacherHeight = Math.max(0, (item.teacherMarks / maxPossible) * 160);
                        const topperHeight = Math.max(0, (item.topperMarks / maxPossible) * 160);

                        const teacherY = 190 - teacherHeight;
                        const topperY = 190 - topperHeight;

                        return (
                          <g key={item.name}>
                            {/* Teacher Bar */}
                            <rect
                              className="bar-hover"
                              x={xCoord}
                              y={teacherY}
                              width={colWidth / 2}
                              height={teacherHeight}
                              fill="#4776e6"
                              rx="2"
                              onMouseEnter={(e) => {
                                const rect = e.target.getBoundingClientRect();
                                const containerRect = e.currentTarget.ownerSVGElement.parentElement.getBoundingClientRect();
                                setHoveredBar({
                                  x: rect.left - containerRect.left + rect.width / 2,
                                  y: rect.top - containerRect.top - 8,
                                  value: item.teacherMarks,
                                  label: "Your Score",
                                  total: item.totalMarks
                                });
                              }}
                              onMouseLeave={() => setHoveredBar(null)}
                            />
                            {/* Topper Bar */}
                            <rect
                              className="bar-hover"
                              x={xCoord + colWidth / 2 + 2}
                              y={topperY}
                              width={colWidth / 2}
                              height={topperHeight}
                              fill="#8e54e9"
                              rx="2"
                              onMouseEnter={(e) => {
                                const rect = e.target.getBoundingClientRect();
                                const containerRect = e.currentTarget.ownerSVGElement.parentElement.getBoundingClientRect();
                                setHoveredBar({
                                  x: rect.left - containerRect.left + rect.width / 2,
                                  y: rect.top - containerRect.top - 8,
                                  value: item.topperMarks,
                                  label: "Topper Score",
                                  total: item.totalMarks
                                });
                              }}
                              onMouseLeave={() => setHoveredBar(null)}
                            />
                            {/* Section Name under column */}
                            <text
                              x={xCoord + colWidth / 2}
                              y="210"
                              textAnchor="middle"
                              fontSize="7.5"
                              fontWeight="600"
                              fill="#495057"
                            >
                              {item.name}
                            </text>
                          </g>
                        );
                      })
                    })()}

                    {/* Left Axis */}
                    <line x1="50" y1="30" x2="50" y2="190" stroke="#adb5bd" strokeWidth="1" />
                    {/* Bottom Axis */}
                    <line x1="50" y1="190" x2="580" y2="190" stroke="#adb5bd" strokeWidth="1" />
                  </svg>

                  {hoveredBar && (
                    <div
                      style={{
                        position: "absolute",
                        left: `${hoveredBar.x}px`,
                        top: `${hoveredBar.y}px`,
                        transform: "translate(-50%, -100%)",
                        backgroundColor: "#1e3c72",
                        color: "#ffffff",
                        padding: "6px 12px",
                        borderRadius: "6px",
                        fontSize: "0.75rem",
                        fontWeight: "bold",
                        pointerEvents: "none",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        zIndex: 10,
                        whiteSpace: "nowrap",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "2px"
                      }}
                    >
                      <span style={{ fontSize: "0.65rem", opacity: 0.8, textTransform: "uppercase" }}>{hoveredBar.label}</span>
                      <span>{hoveredBar.value} / {hoveredBar.total}</span>
                      {/* Tooltip arrow */}
                      <div
                        style={{
                          position: "absolute",
                          bottom: "-4px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: 0,
                          height: 0,
                          borderLeft: "4px solid transparent",
                          borderRight: "4px solid transparent",
                          borderTop: "4px solid #1e3c72"
                        }}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                  <Typography style={{ color: "#6c757d" }}>No section statistics available</Typography>
                </div>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "center", gap: "24px", marginTop: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <div style={{ width: "12px", height: "12px", borderRadius: "3px", backgroundColor: "#4776e6" }} />
                <Typography variant="body2" style={{ fontWeight: 600, color: "#495057" }}>
                  Your Score
                </Typography>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <div style={{ width: "12px", height: "12px", borderRadius: "3px", backgroundColor: "#8e54e9" }} />
                <Typography variant="body2" style={{ fontWeight: 600, color: "#495057" }}>
                  Topper Score
                </Typography>
              </div>
            </div>
          </Paper>
        </Grid>
      </Grid>



      {/* Topic Breakdown Table */}
      <Grid container style={{ marginBottom: "24px", maxWidth: "100%" }}>
        <Grid item xs={12} style={{ width: "100%" }}>
          <Paper sx={{ ...classes.card, padding: "24px" }}>
            <Typography variant="h5" style={{ fontWeight: 700, marginBottom: "20px", color: "#1e3c72" }}>
              Topic Breakdown
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow style={{ backgroundColor: "#f8f9fa" }}>
                    <TableCell style={{ fontWeight: "bold", color: "#1e3c72" }}>Topic Name</TableCell>
                    <TableCell align="center" style={{ fontWeight: "bold", color: "#1e3c72" }}>Total</TableCell>
                    <TableCell align="center" style={{ fontWeight: "bold", color: "#2e7d32" }}>Correct</TableCell>
                    <TableCell align="center" style={{ fontWeight: "bold", color: "#c62828" }}>Incorrect</TableCell>
                    <TableCell align="center" style={{ fontWeight: "bold", color: "#f59e0b" }}>Not Answered</TableCell>
                    <TableCell align="center" style={{ fontWeight: "bold", color: "#1e3c72" }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topicBreakdown.map((row) => {
                    let badgeColor = "#f1f3f5";
                    let badgeText = "#6c757d";
                    if (row.statusCategory === "Strong") {
                      badgeColor = "#e8f5e9";
                      badgeText = "#2e7d32";
                    } else if (row.statusCategory === "Moderate") {
                      badgeColor = "#fff3e0";
                      badgeText = "#e65100";
                    } else if (row.statusCategory === "Weak") {
                      badgeColor = "#ffebee";
                      badgeText = "#c62828";
                    }

                    return (
                      <TableRow key={row.topicName}>
                        <TableCell style={{ fontWeight: 600, color: "#495057" }}>{row.topicName}</TableCell>
                        <TableCell align="center" style={{ fontWeight: 600 }}>{row.count}</TableCell>
                        <TableCell align="center" style={{ fontWeight: 600, color: "#2e7d32" }}>{row.correctCount}</TableCell>
                        <TableCell align="center" style={{ fontWeight: 600, color: "#c62828" }}>{row.incorrectCount}</TableCell>
                        <TableCell align="center" style={{ fontWeight: 600, color: "#f59e0b" }}>{row.notAttempted}</TableCell>
                        <TableCell align="center">
                          <span style={{
                            backgroundColor: badgeColor,
                            color: badgeText,
                            padding: "6px 12px",
                            borderRadius: "12px",
                            fontWeight: 700,
                            fontSize: "0.8rem",
                            textTransform: "uppercase"
                          }}>
                            {row.statusCategory}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Questions list and search/filter bar */}
      <Paper className={classes.card} style={{ padding: "24px" }}>
        <Typography variant="h5" style={{ fontWeight: 700, marginBottom: "20px", color: "#1e3c72" }}>
          Question-by-Question Response Report
        </Typography>

        {/* Filter bar */}
        <Grid container spacing={2} style={{ marginBottom: "24px" }} alignItems="center">
          <Grid item xs={12} sm={6} md={8}>
            <TextField
              placeholder="Search question text..."
              variant="outlined"
              size="small"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon style={{ color: "#adb5bd", marginRight: 8 }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="All">All Questions</MenuItem>
                <MenuItem value="Correct">Correct</MenuItem>
                <MenuItem value="Incorrect">Incorrect</MenuItem>
                <MenuItem value="Unattempted">Unattempted</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Questions Render List */}
        <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
          {filteredQuestions.map((item, idx) => {
            const selectedOptId = (item.selectedOptionIds && item.selectedOptionIds[0]) || (item.selected_option_ids && item.selected_option_ids[0]);
            const correctOptId = (item.correctOptionIds && item.correctOptionIds[0]) || (item.correct_option_ids && item.correct_option_ids[0]);
            const isCorrect = item.status === "Correct";
            const isUnattempted = item.status === "Unattempted";

            let correctIndex = item.options ? item.options.findIndex(o => correctOptId !== undefined && correctOptId !== null && String(o.id) === String(correctOptId)) : -1;
            if (correctIndex === -1 && item.options) {
              correctIndex = item.options.findIndex(o => o.correct_option === true || o.correct_option === "true" || o.correct_option === 1);
            }
            const correctLetter = correctIndex !== -1 ? String.fromCharCode(65 + correctIndex) : "";
            
            const selectedIndex = item.options ? item.options.findIndex(o => selectedOptId !== undefined && selectedOptId !== null && String(o.id) === String(selectedOptId)) : -1;
            const selectedLetter = (isUnattempted || selectedIndex === -1) ? "" : String.fromCharCode(65 + selectedIndex);

            return (
              <div
                key={item.id || idx}
                style={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "24px",
                  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.02)",
                  overflow: "hidden"
                }}
              >
                {/* Purple Question Header Banner */}
                <div style={{
                  backgroundColor: "#e8ebf7",
                  padding: "16px 24px",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  color: "#0d244a",
                  fontWeight: "bold",
                  fontSize: "0.875rem",
                  borderBottom: "1px solid #dcdfe9"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-clipboard-edit"><rect width="8" height="4" x="8" y="2" rx="1" ry="1" /><path d="M10.42 12.67a1 1 0 0 0-.42.83V16a1 1 0 0 0 1 1h2.5a1 1 0 0 0 .83-.42l3.42-4.92a1 1 0 0 0-.83-1.66H14.5a1 1 0 0 0-1 .83Z" /><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /></svg>
                    Question {item.index} / {processedQuestions.length}
                  </div>
                  {item.id && (
                    <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", fontFamily: "monospace" }}>
                      Question ID: {item.id}
                    </span>
                  )}
                </div>

                <div style={{ padding: "24px 32px", display: "flex", flexDirection: "column", gap: "24px" }}>
                  {/* Question Content */}
                  <div
                    style={{ fontSize: "1.05rem", color: "#1e293b", fontWeight: 500, lineHeight: 1.6 }}
                    dangerouslySetInnerHTML={{ __html: item.question_en }}
                  />

                  {/* Hindi Translation if available */}
                  {item.question_hi && (
                    <div
                      style={{ fontSize: "1.05rem", color: "#475569", fontWeight: 500, lineHeight: 1.6, borderTop: "1px dashed #e2e8f0", paddingTop: "12px" }}
                      dangerouslySetInnerHTML={{ __html: item.question_hi }}
                    />
                  )}

                  {/* Vertical Options List */}
                  {item.question_type !== "Fill In The Blanks" ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                      {item.options && item.options.map((option, oIdx) => {
                        const isOptionSelected = selectedOptId !== undefined && selectedOptId !== null && String(option.id) === String(selectedOptId);
                        const isOptionCorrect = (correctOptId !== undefined && correctOptId !== null && String(option.id) === String(correctOptId)) || option.correct_option === true;
                        const optionLetter = String.fromCharCode(65 + oIdx);

                        let borderStyle = "1px solid #e2e8f0";
                        let bgStyle = "#ffffff";
                        let rightIcon = null;

                        if (isOptionCorrect) {
                          borderStyle = "2px solid #a7f3d0";
                          bgStyle = "#f0fdf4";
                          rightIcon = (
                            <span style={{
                              width: "20px",
                              height: "20px",
                              borderRadius: "50%",
                              backgroundColor: "#22c55e",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#ffffff",
                              flexShrink: 0,
                              boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                              marginLeft: "auto"
                            }}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                            </span>
                          );
                        } else if (isOptionSelected) {
                          borderStyle = "2px solid #fecaca";
                          bgStyle = "#fef2f2";
                          rightIcon = (
                            <span style={{
                              width: "20px",
                              height: "20px",
                              borderRadius: "50%",
                              backgroundColor: "#ef4444",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#ffffff",
                              flexShrink: 0,
                              boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                              marginLeft: "auto"
                            }}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </span>
                          );
                        }

                        return (
                          <div
                            key={option.id || oIdx}
                            style={{
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                              gap: "16px",
                              padding: "16px",
                              border: borderStyle,
                              backgroundColor: bgStyle,
                              borderRadius: "12px",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.01)",
                              boxSizing: "border-box"
                            }}
                          >
                            <span style={{
                              padding: "6px 12px",
                              backgroundColor: "#f1f5f9",
                              borderRadius: "8px",
                              fontWeight: 900,
                              fontSize: "0.75rem",
                              color: "#64748b",
                              border: "1px solid rgba(226, 232, 240, 0.5)"
                            }}>
                              {optionLetter}
                            </span>
                            <div
                              style={{ fontSize: "0.875rem", fontWeight: 600, flex: 1 }}
                              dangerouslySetInnerHTML={{ __html: option.option_en }}
                            />
                            {rightIcon}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                      padding: "16px",
                      backgroundColor: "#f8f9fa",
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0"
                    }}>
                      <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#1e3c72" }}>
                        Your Response:
                      </Typography>
                      <div style={{
                        padding: "12px 16px",
                        backgroundColor: isCorrect ? "#f0fdf4" : "#fef2f2",
                        border: isCorrect ? "1px solid #a7f3d0" : "1px solid #fecaca",
                        borderRadius: "8px",
                        fontSize: "0.95rem",
                        fontWeight: 600,
                        color: isCorrect ? "#16a34a" : "#dc2626"
                      }}>
                        {item.answer || <span style={{ fontStyle: "italic", color: "#94a3b8" }}>No response entered</span>}
                      </div>
                    </div>
                  )}

                  {/* Feedback Explanation Footer */}
                  <div style={{ borderTop: "1px solid #f1f5f9", marginTop: "16px", paddingTop: "16px" }}>
                    {(() => {
                      const correctOption = item.options ? item.options.find(o => o.correct_option === true || o.correct_option === "true" || o.correct_option === 1) : null;
                      const correctAnswerText = correctOption ? (correctOption.solution_en || correctOption.solution_hi || correctOption.option_en || correctOption.option_hi) : "";

                      if (isUnattempted) {
                        return (
                          <>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#d97706", fontWeight: "bold", fontSize: "0.875rem", marginBottom: "4px" }}>
                              <span style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#f59e0b", display: "inline-block" }} />
                              You did not attempt this question.
                            </div>
                            <p style={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 500, margin: 0 }}>
                              {item.question_type === "Fill In The Blanks" ? (
                                <>
                                  <b>Correct Answer:</b> {correctAnswerText}
                                </>
                              ) : (
                                <>
                                  <b>Answer:</b> The correct option is {correctLetter}
                                </>
                              )}
                            </p>
                          </>
                        );
                      } else if (isCorrect) {
                        return (
                          <>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#16a34a", fontWeight: "bold", fontSize: "0.875rem", marginBottom: "4px" }}>
                              <span style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#22c55e", display: "inline-block" }} />
                              Congratulations! Your Answer Is Correct.
                            </div>
                            <p style={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 500, margin: 0 }}>
                              {item.question_type === "Fill In The Blanks" ? (
                                <>
                                  <b>Correct Answer:</b> {correctAnswerText} and you've entered: {item.answer}
                                </>
                              ) : (
                                <>
                                  <b>Answer:</b> The correct option is {correctLetter} and you've selected: {selectedLetter}
                                </>
                              )}
                            </p>
                          </>
                        );
                      } else {
                        return (
                          <>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#dc2626", fontWeight: "bold", fontSize: "0.875rem", marginBottom: "4px" }}>
                              <span style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#ef4444", display: "inline-block" }} />
                              Oops! Your Answer Is Wrong.
                            </div>
                            <p style={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 500, margin: 0 }}>
                              {item.question_type === "Fill In The Blanks" ? (
                                <>
                                  <b>Correct Answer:</b> {correctAnswerText} and you've entered: {item.answer}
                                </>
                              ) : (
                                <>
                                  <b>Answer:</b> The correct option is {correctLetter} and you've selected: {selectedLetter}
                                </>
                              )}
                            </p>
                          </>
                        );
                      }
                    })()}

                    {/* Explanations */}
                    {(item.description_en || item.description_hi) && (
                      <Box style={{ marginTop: "16px", padding: "16px", backgroundColor: "#f8f9fa", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}>
                          <InfoOutlinedIcon style={{ color: "#1e3c72", fontSize: 18 }} />
                          <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#1e3c72" }}>
                            Explanation / Solution:
                          </Typography>
                        </div>
                        {item.description_en && (
                          <div
                            style={{ fontSize: "0.9rem", color: "#495057" }}
                            dangerouslySetInnerHTML={{ __html: item.description_en }}
                          />
                        )}
                        {item.description_hi && (
                          <div
                            style={{ fontSize: "0.9rem", color: "#495057", marginTop: "8px" }}
                            dangerouslySetInnerHTML={{ __html: item.description_hi }}
                          />
                        )}
                      </Box>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredQuestions.length === 0 && (
          <div style={{ padding: "40px", textAlign: "center" }}>
            <InfoOutlinedIcon style={{ fontSize: 48, color: "#adb5bd", marginBottom: "12px" }} />
            <Typography variant="body1" style={{ color: "#6c757d" }}>
              No questions found matching your search criteria.
            </Typography>
          </div>
        )}
      </Paper>

      {/* Floating Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className="print-hidden"
        style={{
          position: "fixed",
          bottom: "24px",
          right: "24px",
          padding: "12px",
          backgroundColor: "#0d244a",
          color: "#ffffff",
          borderRadius: "50%",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          border: "none",
          cursor: "pointer",
          transition: "all 0.3s",
          display: showScrollTop ? "block" : "none",
          zIndex: 50
        }}
        aria-label="Scroll to top"
      >
        <ArrowUpwardIcon style={{ fontSize: 24 }} />
      </button>
    </div>
  );
}
